'use client'

import Reveal from '@/components/Reveal'
import {
  LandingShell, Hero, SystemsStrip, FeatureGrid, HowItWorks, InControl, FaqSection, FinalCta, PRICE_ANSWER,
} from '@/components/landing/ExtensionLanding'

// Landing page for graduate scheme and entry-level applications (the
// autumn and January pushes). Claims stay within what was tested: it fills
// the application form; online tests, video interviews and assessment
// centres are the applicant's.

const SYSTEMS = ['Oleeo', 'Workday', 'SAP SuccessFactors', 'Taleo', 'Greenhouse', 'Lever', 'SmartRecruiters', 'Workable']

export default function GraduateApplicationsPageClient() {
  return (
    <LandingShell>
      <Hero
        badge="Built for graduate applications"
        title="Graduate scheme applications,"
        highlight="without retyping your degree every time."
        intro="Every graduate scheme form wants your degree and predicted grade, your A-levels and GCSEs, your part-time jobs and a set of written questions. CVCircuit fills in the factual parts from your Career Bank, so your time goes on the answers that win the place."
        example={{
          step: 'Education',
          label: "Example of a graduate application form's education section, filled in by CVCircuit",
          table: {
            columns: ['Qualification', 'Subject', 'Grade', 'Year'],
            rows: [
              ['BSc (Hons)', 'Economics', '2:1 (predicted)', '2027'],
              ['A level', 'Mathematics', 'A', '2024'],
              ['A level', 'Economics', 'A', '2024'],
              ['GCSE', 'English Language', '7', '2022'],
            ],
          },
          questions: [
            { q: 'Do you have the right to work in the UK?', a: 'The answer you set in your Career Bank' },
            { q: 'Will you require visa sponsorship to take up this role?', a: 'The answer you set in your Career Bank' },
          ],
        }}
      />

      <SystemsStrip
        heading="Tested on the systems graduate employers use"
        systems={SYSTEMS}
        note="It fills in the application form itself. Online tests, video interviews and assessment centres are separate steps, and they're yours."
      />

      <FeatureGrid
        title="The parts of every application"
        highlight="that never change"
        intro="Fill in your Career Bank once. Every scheme after that gets the same accurate answers, and you spend your time on why you want that scheme."
        items={[
          { title: 'Your education, in detail', body: 'Your degree with its predicted or final grade, then your A-levels and GCSEs subject by subject, with grades and dates.' },
          { title: 'Every part-time job', body: 'Bar work, retail, internships and volunteering, with dates and what you did, entered into each form’s own sections.' },
          { title: 'Right to work and sponsorship', body: 'Answered the same way on every form from your Career Bank. Important if you’re on a Student or Graduate visa.' },
          { title: 'Equality and social mobility', body: "The school type, free school meals and first-generation questions many schemes ask, answered only from what you've chosen to share." },
          { title: 'Written questions (Pro)', body: 'First drafts of answers like "Why this scheme?" from your own Career Bank, for you to make your own.' },
          { title: 'The questions that come back', body: "Answer a question once and save it. When the next scheme asks the same thing, it's reused." },
        ]}
      />

      {/* Graduate season */}
      <section className="bg-gray-900 text-white">
        <div className="container mx-auto px-4 py-14 sm:py-20">
          <div className="max-w-4xl mx-auto grid gap-10 md:grid-cols-2 md:items-center">
            <Reveal>
              <h2 className="text-2xl sm:text-3xl font-bold">
                Applying to lots of schemes? <span className="text-primary-400">Start early.</span>
              </h2>
              <p className="mt-4 text-gray-300">
                Many graduate schemes open in the autumn, and some close early once they have enough applicants. The forms are long and repetitive, which is exactly where the hours go.
              </p>
            </Reveal>
            <Reveal delayMs={120}>
              <ul className="space-y-4">
                {[
                  'Fill in your Career Bank once, including predicted grades.',
                  'Let CVCircuit fill the education, work and personal sections of each form.',
                  'Spend the time you save on the written questions and test practice.',
                  'Check every page and submit it yourself.',
                ].map((line) => (
                  <li key={line} className="flex gap-3 text-gray-200">
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary-500/20 text-primary-300 text-xs">✓</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      <HowItWorks />
      <InControl />

      <FaqSection
        faqs={[
          { q: 'Does it do the online tests or video interviews?', a: "No. It fills in application forms. Online tests, video interviews and assessment centres are yours, and you'll have more time to prepare for them." },
          { q: "I haven't graduated yet. What about my grade?", a: 'Add your degree to your Career Bank with its predicted grade, and CVCircuit uses that until you update it with your final result.' },
          { q: 'Will it write my answer to "Why do you want to join this scheme?"', a: "With Pro, it drafts an answer from your Career Bank for you to edit. Employers want your own reasons, so treat it as a first draft and make it yours." },
          { q: "I'm an international student. Will it answer the sponsorship questions correctly?", a: 'It answers them from your Career Bank, so set your right-to-work and sponsorship answers there carefully, especially if your visa has an end date, and every form gets the same answer.' },
          { q: 'What does it cost?', a: PRICE_ANSWER },
          { q: 'Does it work on my phone?', a: "Not yet. It's a Chrome extension, so it needs Google Chrome on a laptop or desktop computer. On a phone, you can email yourself the link to open later on your computer." },
        ]}
      />

      <FinalCta
        title="More applications, less retyping"
        body="Add CVCircuit to Chrome, set up your Career Bank once, and let it fill in the boxes on every scheme you apply to."
      />
    </LandingShell>
  )
}
